import api from "crqlar-react-common/dist/services/api";
import type {Page} from "crqlar-react-common/dist/models/common/page.model";

/**
 * A till that signs itself in with a device key. Mirrors `PosDeviceV3` in `crqlar-api-v3`.
 *
 * `deviceKey` is present only in the response that creates or rotates the key — it cannot be read
 * back afterwards, only rotated.
 */
export interface PosDevice {
    id?: string;
    name?: string;
    deviceKey?: string;
    deviceKeyPub?: string;
    userId?: string;
    simphonyConfigId?: string;
    rvcRef?: string;
    sectorId?: string;
    /** Explicit override. Leave empty and use `landingAppId` so crqlar builds the floor plan link. */
    landingPath?: string;
    /** Organization app id of the restaurant whose floor plan the till opens. */
    landingAppId?: string;
    /** Root restaurant resource within that app. Optional — the floor plan opens on its first one. */
    landingRestaurantId?: string;
    /** Simphony workstation name, the key under which the device goes into the EMC snippet. */
    workstationName?: string;
    lastSeenWorkstation?: string;
    lastSeenAt?: string;
    revokedAt?: string;
    updateDate?: string;
}

const URI = "/v3/pos/device";

export const PosDeviceService = {
    async list(): Promise<Page<PosDevice>> {
        const response = await api.post<Page<PosDevice>>(`${URI}/list`, {
            limit: {offset: 0, limit: 500, sort: [{field: "name", direction: "ASC"}]}
        });
        return response.data;
    },

    async add(device: PosDevice): Promise<PosDevice> {
        const response = await api.post<PosDevice>(`${URI}/add`, device);
        return response.data;
    },

    async update(device: PosDevice): Promise<PosDevice> {
        const response = await api.post<PosDevice>(`${URI}/update`, device);
        return response.data;
    },

    async rotateKey(id: string): Promise<PosDevice> {
        const response = await api.post<PosDevice>(`${URI}/rotateKey`, {id: [id]});
        return response.data;
    },

    async revoke(id: string): Promise<void> {
        await api.post(`${URI}/revoke`, {id: [id]});
    },
};

/**
 * The `crqlarTills` content for EMC (Extension Application `CRQLAR`, 23 - Text), read by
 * `simphony-page-control-shell/crqlar-ops.js`.
 *
 * `appUrl` is this dashboard's own origin, so the same screen produces a sandbox snippet on sandbox
 * and a production snippet on production — nothing environment-specific is typed by hand.
 */
export function buildTillsSnippet(tills: Record<string, string>, appUrl = window.location.origin): string {
    return JSON.stringify({appUrl, tills}, null, 2);
}
